const HHC_CART_UPSELL = {
  addSelector: '[data-hhc-upsell-add]',
  cardSelector: '[data-hhc-upsell-card]',
  variantSelector: '[data-hhc-variant-select]',
  messageSelector: '[data-hhc-upsell-message]',
  drawerInnerSelector: '#cart-drawer .cart-drawer__inner',
  sectionKey: 'cart-drawer-section',
};

const getShopifyRoot = () => {
  return window.Shopify?.routes?.root || '/';
};

const setButtonState = (button, state) => {
  if (!(button instanceof HTMLButtonElement)) return;

  const defaultLabel = button.dataset.defaultLabel || '+ Add';

  switch (state) {
    case 'loading':
      button.disabled = true;
      button.textContent = 'Adding…';
      button.dataset.loading = 'true';
      break;

    case 'success':
      button.disabled = true;
      button.textContent = 'Added ✓';
      button.dataset.loading = 'false';
      break;

    case 'error':
      button.disabled = false;
      button.textContent = 'Try again';
      button.dataset.loading = 'false';
      break;

    default:
      button.disabled = false;
      button.textContent = defaultLabel;
      button.dataset.loading = 'false';
      break;
  }
};

const setMessage = (card, message = '') => {
  if (!(card instanceof HTMLElement)) return;

  const messageElement = card.querySelector(
    HHC_CART_UPSELL.messageSelector
  );

  if (messageElement) {
    messageElement.textContent = message;
  }
};

const updateHeaderCartCount = (drawerInner) => {
  if (!(drawerInner instanceof HTMLElement)) return;

  const drawerBadge = drawerInner.querySelector('.theme-drawer__badge');

  if (!drawerBadge) return;

  const count = drawerBadge.textContent.trim();

  if (!count) return;

  document.querySelectorAll('.cart-bubble__text-count').forEach((element) => {
    element.textContent = count;
  });

  document.querySelectorAll('cart-icon').forEach((element) => {
    element.classList.add('header-actions__cart-icon--has-cart');
  });

  const liveRegion = document.querySelector(
    '[data-testid="cart-count-live-region"]'
  );

  if (liveRegion) {
    liveRegion.textContent = `Cart updated. ${count} items.`;
  }
};

const refreshDrawerFromSection = (sectionHtml) => {
  if (!sectionHtml) return false;

  const parser = new DOMParser();
  const parsedDocument = parser.parseFromString(sectionHtml, 'text/html');

  const newDrawerInner = parsedDocument.querySelector(
    '.cart-drawer__inner'
  );

  const currentDrawerInner = document.querySelector(
    HHC_CART_UPSELL.drawerInnerSelector
  );

  if (!newDrawerInner || !currentDrawerInner) {
    return false;
  }

  updateHeaderCartCount(newDrawerInner);

  currentDrawerInner.replaceWith(newDrawerInner);

  return true;
};

const getVariantId = (card, button) => {
  if (!(card instanceof HTMLElement)) return null;
  if (!(button instanceof HTMLButtonElement)) return null;

  const variantSelect = card.querySelector(
    HHC_CART_UPSELL.variantSelector
  );

  if (variantSelect instanceof HTMLSelectElement) {
    return variantSelect.value;
  }

  return button.dataset.variantId || null;
};

const addUpsellProduct = async (button) => {
  if (!(button instanceof HTMLButtonElement)) return;

  if (button.dataset.loading === 'true') return;

  const card = button.closest(HHC_CART_UPSELL.cardSelector);

  if (!(card instanceof HTMLElement)) return;

  const variantId = getVariantId(card, button);

  if (!variantId) {
    setButtonState(button, 'error');
    setMessage(card, 'Please choose an option.');
    return;
  }

  const variantSelect = card.querySelector(
    HHC_CART_UPSELL.variantSelector
  );

  setMessage(card);
  setButtonState(button, 'loading');

  if (variantSelect instanceof HTMLSelectElement) {
    variantSelect.disabled = true;
  }

  const root = getShopifyRoot();

  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: 1,
          },
        ],
        sections: [HHC_CART_UPSELL.sectionKey],
        sections_url: window.location.pathname,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data?.description ||
          data?.message ||
          'Unable to add this product.'
      );
    }

    const sectionHtml =
      data?.sections?.[HHC_CART_UPSELL.sectionKey] || null;

    setButtonState(button, 'success');
    setMessage(card, 'Added to cart ✓');

    // The cart page totals and item rows live outside the cart drawer.
    // Refresh that page after a successful add; keep the drawer-only
    // update for other storefront pages.
    if (/(?:^|\/)cart\/?$/.test(window.location.pathname)) {
      window.location.reload();
      return;
    }

    if (sectionHtml) {
      window.setTimeout(() => {
        const refreshed = refreshDrawerFromSection(sectionHtml);

        if (!refreshed) {
          window.location.reload();
        }
      }, 250);
    } else {
      window.setTimeout(() => {
        window.location.reload();
      }, 350);
    }
  } catch (error) {
    console.error('[HHC Cart Upsell]', error);

    setButtonState(button, 'error');

    setMessage(
      card,
      error instanceof Error
        ? error.message
        : 'Could not add. Please try again.'
    );

    if (variantSelect instanceof HTMLSelectElement) {
      variantSelect.disabled = false;
    }

    window.setTimeout(() => {
      setButtonState(button, 'default');
    }, 2500);
  }
};

/* Keep price/compare price in sync with selected Shopify variant after drawer refresh. */
document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches(HHC_CART_UPSELL.variantSelector)) return;
  const option = select.selectedOptions[0];
  const card = select.closest(HHC_CART_UPSELL.cardSelector);
  if (!option || !(card instanceof HTMLElement)) return;
  const price = card.querySelector('[data-hhc-upsell-price]');
  const compare = card.querySelector('[data-hhc-upsell-compare]');
  if (price && option.dataset.hhcPrice) price.textContent = option.dataset.hhcPrice;
  if (compare) {
    const value = option.dataset.hhcCompare || '';
    compare.textContent = value;
    compare.hidden = !value;
  }
});

document.addEventListener('click', (event) => {
  const target = event.target;

  if (!(target instanceof Element)) return;

  const button = target.closest(HHC_CART_UPSELL.addSelector);

  if (!(button instanceof HTMLButtonElement)) return;

  event.preventDefault();

  addUpsellProduct(button);
});